import * as React from 'react'
import { useTranslations } from 'next-intl'
import { AppRoutes } from '@repo/web-utils/app-routes'

import { LFS_URL } from '@web/utils/env'

interface Item {
  name: string
  href: string
}

export enum MenuItemType {
  NO_CHILDREN = 'NO_CHILDREN',
  LINKS_ONLY = 'LINKS_ONLY',
  LINKS_AND_IMAGES = 'LINKS_AND_IMAGES',
  CUSTOM = 'CUSTOM',
}

type NoChildrenMenuItem = Item & {
  type: MenuItemType.NO_CHILDREN
}

type LinksOnlyMenuItem = Item & {
  type: MenuItemType.LINKS_ONLY
  mobileShowAllName?: string
  subMenuItems: Item[]
}

type LinksAndImagesMenuItem = Item & {
  type: MenuItemType.LINKS_AND_IMAGES
  mobileShowAllName?: string
  subMenuItems: (Item & {
    image: string
    subtitle: string
  })[]
}

type CustomMenuItem = Item & {
  type: MenuItemType.CUSTOM
  content: React.JSX.Element
}

type MenuItem = NoChildrenMenuItem | LinksOnlyMenuItem | LinksAndImagesMenuItem | CustomMenuItem

export type MenuItems = MenuItem[]

export const useMenuItems = (): MenuItems => {
  const t = useTranslations('components.studio_header')

  const baseMenuItems: MenuItems = [
    {
      name: t('templates.name'),
      href: AppRoutes.STUDIO_TEMPLATES,
      type: MenuItemType.NO_CHILDREN,
    },
    {
      name: t('features.name'),
      href: AppRoutes.STUDIO_FEATURES,
      type: MenuItemType.LINKS_AND_IMAGES,
      mobileShowAllName: t('features.mobile_show_all'),
      subMenuItems: [
        {
          name: t('features.items.1.title'),
          subtitle: t('features.items.1.copy'),
          href: AppRoutes.STUDIO_FEATURES_BACKGROUND_REMOVER,
          image: `${LFS_URL}/web/pages/studio/features/home-page/Studio_Tool_Transparent_Background_Maker_Thumbnail.png`,
        },
        {
          name: t('features.items.2.title'),
          subtitle: t('features.items.2.copy'),
          href: AppRoutes.STUDIO_FEATURES_PNG_TO_SVG_CONVERTER,
          image: `${LFS_URL}/web/pages/studio/features/home-page/Studio_Tool_SVG_Converter_Thumbnail.png`,
        },
        {
          name: t('features.items.3.title'),
          subtitle: t('features.items.3.copy'),
          href: AppRoutes.STUDIO_FEATURES_MIRROR_IMAGE,
          image: `${LFS_URL}/web/pages/studio/features/home-page/Studio_Tool_Mirror_Image_Tool_Thumbnail.png`,
        },
        {
          name: t('features.items.4.title'),
          subtitle: t('features.items.4.copy'),
          href: AppRoutes.STUDIO_FEATURES_ROTATE_IMAGE,
          image: `${LFS_URL}/web/pages/studio/features/home-page/Studio_Tool_Rotate_Thumbnail.png`,
        },
        {
          name: t('features.items.5.title'),
          subtitle: t('features.items.5.copy'),
          href: AppRoutes.STUDIO_FEATURES_CROP_IMAGE,
          image: `${LFS_URL}/web/pages/studio/features/home-page/Studio_Tool_Crop_Image_Tool_Thumbnail.png`,
        },
        {
          name: t('features.items.6.title'),
          subtitle: t('features.items.6.copy'),
          href: AppRoutes.STUDIO_FEATURES_ADD_TEXT_ON_IMAGE,
          image: `${LFS_URL}/web/pages/studio/features/home-page/Studio_Tool_Add_Text_On_Image_Thumbnail.png`,
        },
        {
          name: t('features.items.7.title'),
          subtitle: t('features.items.7.copy'),
          href: AppRoutes.TOOLS_FONT_GENERATOR,
          image: `${LFS_URL}/web/pages/studio/features/home-page/Studio_Font_Generator.png`,
        },
      ],
    },
  ]

  return baseMenuItems
}
